import type {SeasonalPromptPill, PromptSuggestions, PromptSuggestionsValue} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import type {ReactElement} from 'react';
import {useEffect, useMemo, useState} from 'react';
import styles from './ai-redesign-prompt-suggestions.module.scss';
import {PillButton} from '@Components/pill-button';
import {
  EVENT_PROMPT_PILL_KEY,
  getPromptSuggestionsEnum,
  getPromptSuggestionsPillTitle,
  getPromptSuggestionsReplacementPrompt,
  RECENT_PROMPT_DISPLAY_LENGTH,
  RECENT_PROMPT_DISPLAY_LENGTH_SHORT,
  RECENT_PROMPT_SUGGESTION_PILL_ICON,
  RECENT_PROMPT_SUGGESTION_PILL_KEY,
} from '@Libraries/ai-redesign-prompt-box-library';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {TextSize} from '@Components/text';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {IconProps} from '@Components/icon-v2';
import {Icon} from '@Components/icon-v2';
import {motion} from 'framer-motion';

interface AiRedesignPromptSuggestionsProps {
  handlePromptChange: (prompt: string) => void;
  promptSuggestions?: PromptSuggestions;
  recentPrompt?: string;
  numSuggestionsToShow?: number;
  showRefreshSuggestions?: boolean;
  onRefreshSuggestions?: VoidFunction;
  onRecentPromptSelected?: VoidFunction;
  onPromptSuggestionSelected?: (value: PromptSuggestionsValue) => void;
  seasonalPromptPill?: SeasonalPromptPill;
}

function AiRedesignPromptSuggestions({
  promptSuggestions,
  handlePromptChange,
  recentPrompt,
  numSuggestionsToShow = 4,
  showRefreshSuggestions = false,
  onRefreshSuggestions,
  onRecentPromptSelected,
  onPromptSuggestionSelected,
  seasonalPromptPill,
}: AiRedesignPromptSuggestionsProps): ReactElement {
  const [currentSuggestions, setCurrentSuggestions] = useState<PromptSuggestionsValue[]>([]);
  const [excludedSuggestions, setExcludedSuggestions] = useState<PromptSuggestionsValue[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const suggestionsToRender = useMemo(() => {
    if (!promptSuggestions) {
      return [];
    }

    const allSuggestions = Object.values(getPromptSuggestionsEnum(promptSuggestions)) as PromptSuggestionsValue[];
    const availableSuggestions = allSuggestions.filter((suggestion) => !excludedSuggestions.includes(suggestion));

    let suggestionsToRender: PromptSuggestionsValue[];
    if (availableSuggestions.length > numSuggestionsToShow) {
      const shuffled = [...availableSuggestions].sort(() => Math.random() - 0.5);
      suggestionsToRender = shuffled.slice(0, numSuggestionsToShow);
    } else {
      suggestionsToRender = availableSuggestions;
    }

    return suggestionsToRender;
  }, [promptSuggestions, excludedSuggestions, numSuggestionsToShow, refreshKey]);

  useEffect(() => {
    setCurrentSuggestions(suggestionsToRender);
  }, [suggestionsToRender]);

  const getSuggestionPill = (
    key: string,
    displayText: string,
    replacementText: string,
    iconProps?: IconProps,
    onClickCallback?: () => void,
    displayTextClasses?: string
  ): ReactElement => {
    return (
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <PillButton
          key={key}
          text={displayText}
          size={PillButtonSize.SMALL}
          textSize={TextSize.XXSMALL}
          leftIconProps={iconProps}
          onClick={(_id, e) => {
            e?.stopPropagation();
            handlePromptChange(replacementText);
            onClickCallback?.();
          }}
          textClasses={displayTextClasses}
        />
      </div>
    );
  };

  const getPromptSuggestionPills = (): ReactElement | undefined => {
    if (!promptSuggestions) {
      return;
    }

    return (
      <>
        {suggestionsToRender.map((suggestion: PromptSuggestionsValue) =>
          getSuggestionPill(suggestion, getPromptSuggestionsPillTitle(suggestion), getPromptSuggestionsReplacementPrompt(suggestion), undefined, () =>
            onPromptSuggestionSelected?.(suggestion)
          )
        )}
      </>
    );
  };

  const getRecentPromptPillDisplayText = (originalText: string): string => {
    const displayLength = showRefreshSuggestions ? RECENT_PROMPT_DISPLAY_LENGTH_SHORT : RECENT_PROMPT_DISPLAY_LENGTH;

    if (originalText.length <= displayLength) {
      return originalText;
    }

    return originalText.slice(0, displayLength) + '...';
  };

  const getRecentPrompt = (): ReactElement | undefined => {
    if (!recentPrompt) {
      return;
    }

    return getSuggestionPill(
      RECENT_PROMPT_SUGGESTION_PILL_KEY,
      getRecentPromptPillDisplayText(recentPrompt),
      recentPrompt,
      {
        icon: RECENT_PROMPT_SUGGESTION_PILL_ICON,
        size: IconSize.SIZE_ICON_16,
      },
      () => onRecentPromptSelected?.()
    );
  };

  const getSeasonalPromptPill = (): ReactElement | undefined => {
    if (!seasonalPromptPill) {
      return;
    }

    const prompts = seasonalPromptPill.prompts;
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];

    return getSuggestionPill(EVENT_PROMPT_PILL_KEY, seasonalPromptPill.label, randomPrompt, undefined, undefined, seasonalPromptPill.labelClasses);
  };

  const handleRefreshClick = (): void => {
    if (!promptSuggestions) {
      return;
    }

    const allSuggestions = Object.values(getPromptSuggestionsEnum(promptSuggestions)) as PromptSuggestionsValue[];
    const newExcludedSuggestions = [...excludedSuggestions, ...currentSuggestions];

    const remainingSuggestions = allSuggestions.filter((suggestion) => !newExcludedSuggestions.includes(suggestion));

    if (remainingSuggestions.length < numSuggestionsToShow) {
      setExcludedSuggestions(currentSuggestions);
    } else {
      setExcludedSuggestions(newExcludedSuggestions);
    }

    setRefreshKey((prev) => prev + 1);

    onRefreshSuggestions?.();
  };

  const getRefreshIcon = (): ReactElement | undefined => {
    if (!showRefreshSuggestions) {
      return;
    }

    return (
      <Icon
        type={IconType.SECONDARY}
        shape={IconShape.CIRCLE}
        size={IconSize.SIZE_ICON_16}
        icon="icon-rotate-ccw"
        ariaLabel={window.i18next.t('pmwjs_refresh')}
        onClick={handleRefreshClick}
      />
    );
  };

  const getPromptSuggestions = (): ReactElement => {
    return (
      <motion.div
        style={{overflow: 'hidden'}}
        initial={{opacity: 0, height: 0, y: -8}}
        animate={{opacity: 1, height: 'auto', y: 0}}
        exit={{opacity: 0, height: 0, y: -8}}
        transition={{duration: 0.2, ease: 'easeInOut'}}
      >
        <div className={styles.promptSuggestions}>
          {getRefreshIcon()}
          {getRecentPrompt()}
          {getSeasonalPromptPill()}
          {getPromptSuggestionPills()}
        </div>
      </motion.div>
    );
  };

  return getPromptSuggestions();
}

export default AiRedesignPromptSuggestions;
